import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  message,
  Modal,
  Row,
  Select,
  Spin,
  Table,
} from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { AuthContext } from "../../Auth/AuthContext";
import { getErrorMessage } from "../../utils/getErrorMessage";
import {
  addFilter,
  deleteFilter,
  getallDeveloperForTask,
  getAllReports,
  getFilter,
  updateFilter,
} from "../../utils/API";
import * as Icons from "@ant-design/icons";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { MdHeight } from "react-icons/md";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

interface Developer {
  _id: string;
  username: string;
  developerNumber: string;
}

const FilterTab = ({
  f,
  index,
  totalTabs,
  isActive,
  defaultFilterId,
  applyFavouriteFilter,
  setAsDefaultFilter,
}: any) => {
  const [isHovered, setIsHovered] = useState(false);

  const maxWords = 2;
  const words = f.name.split(" ");
  const isLong = words.length > maxWords;
  const displayText = isLong
    ? words.slice(0, maxWords).join(" ") + "..."
    : f.name;

  const content = (
    <div
      key={f._id}
      onClick={() => applyFavouriteFilter(f)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`flex-0 snap-start flex-shrink-0 text-center cursor-pointer font-medium px-2 py-2 whitespace-nowrap overflow-hidden text-ellipsis relative pr-6 ${
        isActive ? "bg-[#001529] text-white" : "bg-white text-gray-700"
      }`}
      style={{
        width: "calc(100% / 6)",
        minWidth: "0",
        borderRight: index !== totalTabs - 1 ? "1px solid #e5e7eb" : "none",
      }}
    >
      <span>{displayText}</span>
      <div
        className={`absolute right-1 top-1/2 -translate-y-1/2 transition-opacity duration-200 ${
          defaultFilterId === f._id || isHovered ? "opacity-100" : "opacity-0"
        }`}
        onClick={(e) => setAsDefaultFilter(e, f)}
        title="Set as Default"
      >
        {defaultFilterId === f._id ? (
          <Icons.PushpinFilled className="text-yellow-500 text-sm" />
        ) : (
          <Icons.PushpinOutlined
            className={`${
              isActive ? "text-gray-300" : "text-gray-400"
            } hover:text-blue-400 text-sm`}
          />
        )}
      </div>
    </div>
  );

  return isLong ? <Tooltip title={f.name}>{content}</Tooltip> : content;
};

const ReportPage = () => {
  const { authData } = useContext(AuthContext);
  const isDeveloper = authData.roleId?.roleId === 2;
  const userId = authData?.user?._id;
  const [loading, setLoading] = useState(false);
  const [developers, setDevelopers] = useState<Developer[]>([]);
  const [selectedDeveloper, setSelectedDeveloper] = useState<string | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const todayDate = new Date().getDate();
  const [selectedDay, setSelectedDay] = useState<number | null>(todayDate);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [selectedMonthNum, setSelectedMonthNum] = useState(dayjs().month() + 1);
  const isCurrentMonthYear =
    selectedMonthNum === dayjs().month() + 1 && selectedYear === dayjs().year();
  const [reports, setReports] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  //Filter State
  const [ReportModal, setReportModal] = useState(false);
  const [favouriteFilters, setFavouriteFilters] = useState<any[]>([]);
  const [selectedFav, setSelectedFav] = useState<string | null>(null);
  const [defaultFilterId, setDefaultFilterId] = useState<string | null>(
    localStorage.getItem("defaultTaskReportFilterId"),
  );
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [timeFilter, setTimeFilter] = useState<
    "today" | "thisMonth" | "lastMonth" | "custom" | undefined
  >(undefined);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(null);
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [favName, setFavName] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState();
  const [selectedIcon, setSelectedIcon] = useState("");
  const [error, setError] = useState("");

  const saveFilterOrder = async (newOrder: any[]) => {
    try {
      const updatedFilters = newOrder.map((filter, index) => ({
        ...filter,
        orderId: index + 1,
      }));

      // 🔁 Update one by one
      await Promise.all(
        updatedFilters.map((f) =>
          updateFilter(f._id, { orderId: f.orderId, type: "TaskReport" }),
        ),
      );

      // ✅ Update local state + localStorage
      setFavouriteFilters(updatedFilters);
      saveFavouriteFilters(updatedFilters);
    } catch (error) {
      console.error("Failed to save filter order:", error);
    }
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const reordered = Array.from(favouriteFilters);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    saveFilterOrder(reordered); // 👈 updates backend + localStorage
  };

  useEffect(() => {
    const checkScroll = () => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;

      setCanScrollLeft(scrollEl.scrollLeft > 0);
      setCanScrollRight(
        scrollEl.scrollWidth > scrollEl.clientWidth + scrollEl.scrollLeft,
      );
    };

    // Check initially
    checkScroll();

    // Optional: check on window resize
    window.addEventListener("resize", checkScroll);

    return () => window.removeEventListener("resize", checkScroll);
  }, [favouriteFilters]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const onScroll = () => {
      setCanScrollLeft(scrollEl.scrollLeft > 0);
      setCanScrollRight(
        scrollEl.scrollWidth > scrollEl.clientWidth + scrollEl.scrollLeft,
      );
    };

    scrollEl.addEventListener("scroll", onScroll);

    return () => scrollEl.removeEventListener("scroll", onScroll);
  }, []);

  const FetchReports = async (extraParams: any = {}) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        limit: 10,
        timeFilter: timeFilter || undefined,
        startDate: startDate ? startDate.format("YYYY-MM-DD") : undefined,
        endDate: endDate ? endDate.format("YYYY-MM-DD") : undefined,
        developerId: selectedAssignee || "",
        ...extraParams, // override with favourite-specific values
      };

      const res = await getAllReports(params);
      setLoading(false);
      setReports(res.data);
      setTotal(res.pagination.total);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  };

  useEffect(() => {
    if (!filtersInitialized) return;
    FetchReports();
  }, [page, selectedYear, selectedMonthNum, selectedDay, filtersInitialized]);

  useEffect(() => {
    const fetchDevelopers = async () => {
      try {
        const result = await getallDeveloperForTask("", "");
        setDevelopers(result.data);
      } catch (error) {
        message.error("Failed to fetch developers.");
      }
    };
    fetchDevelopers();
  }, []);

  function TaskReportCell({ text }: { text: string }) {
    const divRef = useRef<HTMLDivElement>(null);
    const [isOverflow, setIsOverflow] = useState(false);

    useEffect(() => {
      const el = divRef.current;
      if (!el) return;

      // Wait a tick to ensure innerHTML renders
      setTimeout(() => {
        setIsOverflow(el.scrollHeight > el.clientHeight);
      }, 0);
    }, [text]);

    if (!text) return <span className="text-gray-400">N/A</span>;

    const content = (
      <div
        ref={divRef}
        className="
                    max-h-[48px]
                    overflow-hidden
                    cursor-pointer
                    [display:-webkit-box]
                    [-webkit-line-clamp:2]
                    [-webkit-box-orient:vertical]"
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );

    return isOverflow ? (
      <Tooltip
        title={
          <div
            className="max-h-[200px] overflow-y-auto p-2 description-tooltip-content"
            dangerouslySetInnerHTML={{ __html: text }}
          />
        }
        classNames={{ root: "plain-tooltip" }}
        mouseEnterDelay={0.3}
        placement="top"
        overlayStyle={{
          maxWidth: "500px",
          minWidth: "400px",
          maxHeight: "400px",
        }}
      >
        {content}
      </Tooltip>
    ) : (
      content
    );
  }

  const columns = [
    {
      title: "Developer",
      dataIndex: ["userId", "username"], // nested path when populated
      key: "userName",
      width: 50,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 50,
      render: (date: string | Date) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: "Day",
      dataIndex: "date",
      key: "day",
      width: 50,
      render: (date: string | Date) => dayjs(date).format("dddd"),
    },
    {
      title: "Task Report",
      dataIndex: "taskReport",
      key: "taskReport",
      width: 250,
      render: (text: string) => <TaskReportCell text={text} />,
    },
  ];

  const filterFetch = async () => {
    try {
      const localFilters = getFavouriteFilters(); // ✅ already only Analytics if you fixed earlier
      if (isDeveloper) {
        setFiltersInitialized(true);
        return;
      }

      const response = await getFilter(); // API call

      // ✅ filter server response by Analytics type
      const serverFilters = Array.isArray(response?.data)
        ? response.data.filter((res: any) => res.type === "TaskReport")
        : response?.data?.type === "TaskReport"
          ? [response.data]
          : [];

      // ✅ only keep filters for current user
      const userId = authData?.user?._id;
      const userFilters = serverFilters.filter(
        (sf: any) => sf.userId === userId,
      );

      // ✅ merge local + server (avoid duplicates by _id)
      const mergedFilters = [
        ...localFilters,
        ...userFilters.filter(
          (sf: any) => !localFilters.some((lf) => lf._id === sf._id),
        ),
      ];

      // ✅ save merged into localStorage
      saveFavouriteFilters(mergedFilters);
      setFavouriteFilters(mergedFilters);

      // ✅ Apply default filter on load
      const defaultId = localStorage.getItem("defaultTaskReportFilterId");
      if (defaultId) {
        const defaultFilter = mergedFilters.find((f: any) => f._id === defaultId);
        if (defaultFilter && selectedFav !== defaultFilter._id) {
          setSelectedFav(defaultFilter._id);
          localStorage.setItem("selectedFavId", defaultFilter._id);
          setPage(1);
          applyFavouriteFilter(defaultFilter);
          setFiltersInitialized(true);
          return;
        }
        if (!defaultFilter) {
          localStorage.removeItem("defaultTaskReportFilterId");
          setDefaultFilterId(null);
        }
      }
      setFiltersInitialized(true);
    } catch (error) {
      console.error("Failed to fetch filters:", error);
      setFiltersInitialized(true);
    }
  };

  useEffect(() => {
    filterFetch();
  }, []);

  // ===== REGISTER EMAIL TEMPLATE COLUMNS =====
  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  const timeFilterOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last7days", label: "Last 7 Days" },
    { value: "thisMonth", label: "This Month" },
    { value: "lastMonth", label: "Last Month" },
    { value: "Last3Month", label: "Last 3 Month" },
    { value: "Last6Month", label: "Last 6 Month" },
    ...(startDate && endDate
      ? [
          {
            value: "custom",
            label: `${startDate.format("MMM D")} - ${endDate.format("MMM D")}`,
            startDate: startDate.format("DD-MM-YYYY"),
            endDate: endDate.format("DD-MM-YYYY"),
          },
        ]
      : [
          {
            value: "custom",
            label: "Custom Range",
            startDate: null,
            endDate: null,
          },
        ]),
  ];

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, []);

  const iconKeys = Object.keys(Icons).filter(
    (key) => key.endsWith("Outlined"), // only outlined icons (you can adjust)
  );
  const options = iconKeys.map((key) => {
    const IconComp = (Icons as any)[key];
    return {
      label: (
        <span>
          <IconComp style={{ marginRight: 8 }} /> {key}
        </span>
      ),
      value: key,
    };
  });

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = 180; // width of one tab
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };
  const FILTER_STORAGE_KEY = "TaskReportFilter";

  const getFavouriteFilters = (): any[] => {
    try {
      const saved = localStorage.getItem(FILTER_STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : [];
      return parsed.filter((f: any) => f.type === "TaskReport");
    } catch (error) {
      console.error(
        "Failed to parse favourite filters from localStorage",
        error,
      );
      return [];
    }
  };

  const saveFavouriteFilters = (filters: any[]) => {
    try {
      const onlyReport = filters.filter((f: any) => f.type === "TaskReport");
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(onlyReport));
    } catch (error) {
      console.error("Failed to save favourite filters to localStorage", error);
    }
  };

  const addFavouriteFilter = async (fav: any) => {
    if (!fav.name.trim()) {
      setError("Please enter favorite name!");
      return;
    }
    setError("");

    const filters = getFavouriteFilters();

    const payload = {
      type: "TaskReport",
      userId: userId || null,
      icon: fav.icon || "",
      name: fav.name,
      time: timeFilter || null,
      startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
      endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
      assignees: selectedAssignee
        ? selectedAssignee
        : selectedAssignee
          ? selectedAssignee
          : null,
      isActive: true,
      isDeleted: false,
    };

    try {
      // send payload to backend
      const response = await addFilter(payload);

      // build slug (unique identifier for query params)
      const slug = fav.name.toLowerCase().replace(/\s+/g, "-");

      // build saved filter
      const savedFilter = {
        ...payload,
        _id: response.data?._id,
        slug,
      };

      // save in local storage / state
      // filters.push(savedFilter);
      const updatedFilters = [...filters, savedFilter];
      saveFavouriteFilters(updatedFilters);
      setFavouriteFilters(updatedFilters);

      message.success("Favourite filter saved!");
    } catch (error) {
      console.error("Failed to save filter:", error);
      message.error("Failed to save favourite filter!");
    }
  };

  const deleteFavouriteFilter = async (id: any) => {
    try {
      await deleteFilter(id);

      const filters = getFavouriteFilters().filter((f) => f._id !== id);
      saveFavouriteFilters(filters);
      setFavouriteFilters(filters);

      // ✅ reset selectedFav if deleted
      if (selectedFav === id) {
        setSelectedFav(null);
        localStorage.removeItem("selectedFavId");
      }

      // ✅ reset default if deleted
      if (defaultFilterId === id) {
        setDefaultFilterId(null);
        localStorage.removeItem("defaultTaskReportFilterId");
      }

      message.success("Favourite filter deleted!");
    } catch (error) {
      console.error("Failed to delete filter:", error);
      message.error("Failed to delete favourite filter!");
    }
  };

  const setAsDefaultFilter = (e: React.MouseEvent, fav: any) => {
    e.stopPropagation();
    if (defaultFilterId === fav._id) {
      localStorage.removeItem("defaultTaskReportFilterId");
      setDefaultFilterId(null);
      message.success(`'${fav.name}' removed from default.`);
    } else {
      localStorage.setItem("defaultTaskReportFilterId", fav._id);
      setDefaultFilterId(fav._id);
      message.success(`'${fav.name}' set as default filter!`);
      if (selectedFav !== fav._id) {
        applyFavouriteFilter(fav);
      }
    }
  };
  const applyFavouriteFilter = async (fav: any) => {
    try {
      // ✅ Toggle off if same favourite clicked again
      if (selectedFav === fav._id) {
        setSelectedFav(null);
        localStorage.removeItem("selectedFavId");

        // Reset all filters
        setTimeFilter(undefined);
        setStartDate(null);
        setEndDate(null);
        setSelectedAssignee(undefined);

        // ✅ Important: wait one tick for state to clear before fetching
        setTimeout(() => {
          FetchReports({
            timeFilter: undefined,
            startDate: undefined,
            endDate: undefined,
            developerId: "",
            page: 1,
          });
        }, 0);

        return;
      }

      // ✅ Set new selected favourite
      setSelectedFav(fav._id);
      localStorage.setItem("selectedFavId", fav._id);

      // ✅ Apply its saved filters
      setTimeFilter(fav.time || undefined);
      setStartDate(fav.startDate ? dayjs(fav.startDate) : null);
      setEndDate(fav.endDate ? dayjs(fav.endDate) : null);
      setSelectedAssignee(fav.assignees || "");

      // ✅ Fetch reports with these filters
      setTimeout(() => {
        FetchReports({
          timeFilter: fav.time || undefined,
          startDate: fav.startDate || undefined,
          endDate: fav.endDate || undefined,
          developerId: fav.assignees || "",
          page: 1,
        });
      }, 0);
    } catch (error) {
      console.error("Error applying favourite filter:", error);
    }
  };

  const ReportFilterMenu = (
    <div
      style={{
        display: "flex",
        maxWidth: "750px",
        background: "linear-gradient(145deg, #ffffff, #f8fafc)",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        borderRadius: "12px",
        overflow: "hidden",
        padding: "16px",
        gap: "20px",
        height: "55vh",
      }}
    >
      {/* -------- Favorites Section (Fixed) -------- */}
      <div
        style={{
          flex: "0 0 250px", // fixed width
          padding: "12px",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          background: "#fff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  flex: 1,
                  overflowY: "auto",
                  scrollbarWidth: "none", // Firefox
                  msOverflowStyle: "none", // IE 10+
                }}
              >
                <style>
                  {`
                  div::-webkit-scrollbar {
                    display: none; /* Hide scrollbar for Chrome, Safari, Opera */
                  }
                `}
                </style>
                <Table
                  dataSource={favouriteFilters}
                  rowKey={(record: any) => record._id || record.id}
                  pagination={false}
                  size="small"
                  style={{ marginTop: "12px" }}
                  scroll={{ y: 290 }}
                  className="scrollbar-hide custom-scroll-table"
                  columns={[
                    {
                      title: "Favorites",
                      dataIndex: "name",
                      key: "name",
                      render: (_: any, record: any, index: number) => {
                        const IconComp = (Icons as any)[record.icon];
                        return (
                          <Draggable
                            draggableId={record._id}
                            index={index}
                            key={record._id}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  cursor: "grab",
                                  ...provided.draggableProps.style,
                                }}
                                onClick={() => applyFavouriteFilter(record)}
                              >
                                {IconComp && (
                                  <IconComp style={{ fontSize: "16px" }} />
                                )}
                                <span
                                  style={{
                                    flex: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: "200px",
                                  }}
                                >
                                  {record.name}
                                </span>
                              </div>
                            )}
                          </Draggable>
                        );
                      },
                    },
                    {
                      title: "Action",
                      key: "action",
                      align: "center",
                      width: 80,
                      render: (_: any, record: any) => (
                        <Icons.DeleteOutlined
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteFavouriteFilter(record._id);
                          }}
                          style={{ color: "#ef4444", cursor: "pointer" }}
                        />
                      ),
                    },
                  ]}
                />
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* -------- Filters Section (Scrollable) -------- */}
      <div
        style={{
          flex: 1,
          padding: "12px",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          background: "#fff",
          overflowY: "auto",
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE 10+
        }}
      >
        <style>
          {`
          div::-webkit-scrollbar {
            display: none; /* Hide scrollbar for Chrome, Safari, Opera */
          }
        `}
        </style>

        <h4
          style={{
            fontWeight: 600,
            fontSize: "16px",
            marginBottom: "12px",
            color: "#1f2937",
          }}
        >
          Filters
        </h4>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", gap: "8px" }}>
            {/* --- Icon Select --- */}
            <Tooltip
              title="Select an Icon"
              color="#CCCCCC"
              overlayInnerStyle={{
                color: "#000", // ✅ font color
                padding: "12px",
              }}
            >
              <Select
                style={{ width: 80, marginTop: "5px" }}
                showSearch
                placeholder="Select Icon"
                value={selectedIcon}
                onChange={(val) => setSelectedIcon(val)}
                optionFilterProp="children"
                dropdownMatchSelectWidth={false}
                onClick={(e) => e.stopPropagation()}
                options={options}
              />
              {/* {iconKeys.map((key) => {
                                        const IconComp = (Icons as any)[key];
                                        return (
                                            <Option key={key} value={key}>
                                                <IconComp style={{ marginRight: 8 }} /> {key}
                                            </Option>
                                        );
                                    })} */}
              {/* </Select> */}
            </Tooltip>

            {/* --- Name Input --- */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Input
                placeholder="Enter favorite name"
                className="Allinput"
                value={favName}
                onChange={(e) => setFavName(e.target.value)}
              />
	              {error && (
	                <span
	                  style={{ color: "red", fontSize: "12px", marginTop: "16px" }}
	                >
	                  {getErrorMessage(error)}
	                </span>
	              )}
            </div>
            {/* --- Save Button --- */}
          </div>

          <Select
            style={{ width: "100%", height: "33px" }}
            value={timeFilter}
            onChange={(value, option) => {
              setTimeFilter(value);
              setPage(1);

              if (value === "custom") {
                if (
                  option &&
                  !Array.isArray(option) &&
                  (option as { startDate?: string; endDate?: string })
                    .startDate &&
                  (option as { startDate?: string; endDate?: string }).endDate
                ) {
                  // directly set range from option
                  setStartDate(
                    dayjs((option as { startDate: string }).startDate),
                  );
                  setEndDate(dayjs((option as { endDate: string }).endDate));
                } else {
                  // show picker if not yet selected
                  setShowRangePicker(true);
                }
              } else {
                setStartDate(null);
                setEndDate(null);
                setTempStartDate(null);
                setTempEndDate(null);
                setShowRangePicker(false);
              }
            }}
            placeholder="Select Time Filter"
            allowClear
            options={timeFilterOptions}
            optionLabelProp="label"
            dropdownRender={(menu) => (
              <div>
                {menu}
                <div style={{ textAlign: "center" }}>
                  <Button
                    type="link"
                    style={{ color: "red" }}
                    onClick={() => {
                      setTimeFilter(undefined);
                      setStartDate(null);
                      setEndDate(null);
                      setTempStartDate(null);
                      setTempEndDate(null);
                      setShowRangePicker(false);
                    }}
                  >
                    Clear All Filters
                  </Button>
                </div>
              </div>
            )}
          />
          {showRangePicker && (
            <div
              style={{
                position: "fixed",
                top: "25%",
                left: "70%",
                transform: "translate(-50%, -50%)",
                zIndex: 1500,
                background: "#fff",
                padding: "16px",
                boxShadow:
                  "0 3px 6px -4px rgba(0,0,0,0.12), 0 6px 16px 0 rgba(0,0,0,0.08), 0 9px 28px 8px rgba(0,0,0,0.05)",
                borderRadius: "8px",
                width: "fit-content",
              }}
            >
              <DatePicker.RangePicker
                value={
                  tempStartDate && tempEndDate
                    ? [tempStartDate, tempEndDate]
                    : null
                }
                onChange={(dates) => {
                  setTempStartDate(dates ? dates[0] : null);
                  setTempEndDate(dates ? dates[1] : null);
                }}
                format="YYYY-MM-DD"
                style={{ marginBottom: "16px", width: "100%" }}
                allowClear={false}
                getPopupContainer={(triggerNode) => document.body}
                popupStyle={{ zIndex: 2000 }} // make sure it’s above your box
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <Button
                  onClick={() => {
                    setShowRangePicker(false);
                    setTempStartDate(null);
                    setTempEndDate(null);
                    setTimeFilter(undefined);
                    setStartDate(null);
                    setEndDate(null);
                    setPage(1);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="allbuttons text-white deal-apply"
                  onClick={() => {
                    if (tempStartDate && tempEndDate) {
                      setStartDate(tempStartDate);
                      setEndDate(tempEndDate);
                      setShowRangePicker(false);
                      setPage(1);
                      setTimeFilter("custom");
                    }
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}

          <Select
            placeholder="Select Developer"
            showSearch
            allowClear
            // // onClick={(e) => e.stopPropagation()}
            // style={{ width: '100%' }}
            value={selectedAssignee ?? undefined} // bind state
            onChange={(val) => setSelectedAssignee(val)} // update state
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {developers.map((dev: any) => (
              <Select.Option key={dev._id} value={dev._id}>
                {dev.username}
              </Select.Option>
            ))}
          </Select>

          <div className="flex justify-end mt-1">
            {/* <Tooltip title="Save" color="#CCCCCC"
                      overlayInnerStyle={{
                        color: "#000",   // ✅ font color
                      }} > */}
            <button
              className="allsubmitebutton flex items-center justify-center"
              style={{
                width: "53px",
                height: "30px",
                borderRadius: "6px",
              }}
              type="submit"
              onClick={(e) => {
                e.stopPropagation();
                addFavouriteFilter({
                  name: favName,
                  icon: selectedIcon,
                });

                // clear after save
                setFavName("");
                setSelectedIcon("");
              }}
            >
              Save
            </button>
            {/* </Tooltip> */}
          </div>
        </div>
      </div>
    </div>
  );

  const resetSettings = () => {
    setTimeFilter(undefined);
    setStartDate(null);
    setEndDate(null);
    setTempStartDate(null);
    setShowRangePicker(false);
    setSelectedAssignee(undefined);
    setFavName("");
    setError("");
  };
  return (
    <Spin spinning={loading}>
      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 20 }}>
        <Col xs={20} sm={20} md={20} lg={20}>
          <div className="flex items-center w-full gap-2">
            {canScrollLeft && (
              <div
                className="absolute left-2 z-10 flex items-center "
                style={{ width: "28px", height: "28px" }}
                onClick={() => scroll("left")}
              >
                <Icons.LeftOutlined className="text-gray-500 text-base cursor-pointer" />
              </div>
            )}

            <div
              ref={scrollRef}
              className="flex overflow-x-auto w-full snap-x snap-mandatory"
              style={{
                scrollBehavior: "smooth",
                scrollbarWidth: "none",
              }}
            >
              {favouriteFilters.map((f: any, index: number) => {
                const isActive = selectedFav === f._id;
                return (
                  <FilterTab
                    key={f._id}
                    f={f}
                    index={index}
                    totalTabs={favouriteFilters.length}
                    isActive={isActive}
                    defaultFilterId={defaultFilterId}
                    applyFavouriteFilter={applyFavouriteFilter}
                    setAsDefaultFilter={setAsDefaultFilter}
                  />
                );
              })}
            </div>

            {canScrollRight && (
              <div
                className="absolute right-0 z-10  flex items-center"
                style={{ width: "28px", height: "28px" }}
                onClick={() => scroll("right")}
              >
                <Icons.RightOutlined className="text-gray-500 text-base cursor-pointer" />
              </div>
            )}
          </div>
        </Col>

        <Col xs={5} sm={5} md={4} lg={4}>
          <div className="flex justify-end" style={{ height: "40px" }}>
            <Button
              icon={<Icons.FilterOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setReportModal(true);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                height: "40px",
                padding: "0 16px",
                minWidth: "90px",
                borderRadius: "28px",
                fontWeight: 700,
                fontSize: "15px",
                background: "linear-gradient(135deg, #001529, #1e3a8a)",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(30, 58, 138, 0.45)",
                border: "none",
                letterSpacing: "0.5px",
                // transition: "all 0.25s ease-in-out",
              }}
            >
              Filters
            </Button>

            <Modal
              style={{
                bottom: 20,
                top: 20,
                // maxHeight: 'calc(100vh - 30px)',
                margin: "0 auto",
                position: "relative",
              }}
              styles={{
                body: {
                  overflowY: "auto",
                  fontFamily: "inter, sans-serif",
                },
                header: {
                  padding: "12px",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#f1f5ffff",
                },
                content: {
                  padding: 0,
                  overflow: "hidden",
                },
              }}
              title={
                <div
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "20px",
                    fontWeight: 500,
                  }}
                >
                  Report Favourite Filter
                </div>
              }
              open={ReportModal}
              onCancel={(e) => {
                (e.stopPropagation(), setReportModal(false));
                resetSettings();
              }}
              footer={null}
              width={650}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()} // also block submenu toggling
              >
                {ReportFilterMenu}
              </div>
            </Modal>
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card className="w-full">
            <div className="scrollbar-hide custom-scroll-table">
              <Table
                columns={columns}
                dataSource={reports}
                pagination={{
                  current: page,
                  pageSize: 10,
                  showSizeChanger: false,
                  total: total,
                  onChange: (p) => setPage(p),
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </Spin>
  );
};

export const REPORT_TABLE_COLUMNS = ["username", "date", "taskReport"];

export default ReportPage;

{
  /* </Row> */
}

{
  /* Admin View */
}
{
  /* {authData?.roleId?.roleId === 1 && ( */
}
{
  /* <> */
}
{
  /* Developer Select */
}
{
  /* <Col xs={24} sm={24} md={6} lg={5} xl={4}>
                            <Select
                                showSearch
                                style={{ width: '100%', height: '33px' }}
                                placeholder="Select developer"
                                value={selectedDeveloper}
                                onChange={(value: string) => {
                                    setSelectedDeveloper(value || null);
                                    setPage(1);
                                }}
                                allowClear
                                onClear={() => {
                                    setSelectedDeveloper(null);
                                    setPage(1);
                                }}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.children?.toString()?.toLowerCase() || '').includes(input.toLowerCase())
                                }
                            >
                                {developers.map(dev => (
                                    <Select.Option key={dev._id} value={dev._id}>
                                        {dev.username}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col> */
}

{
  /* Date Selectors */
}
{
  /* <Col xs={24} sm={24} md={16} lg={14} xl={12}>
                            <div className="flex flex-col sm:flex-row gap-2 w-full">
                                <Select
                                    style={{ flex: 1, height: '33px' }}
                                    value={selectedDay || undefined}
                                    onChange={(value) => {
                                        setSelectedDay(value || null);
                                        setPage(1);
                                    }}
                                    placeholder={isCurrentMonthYear ? dayjs().date().toString() : "Select Day"}
                                    allowClear
                                    onClear={() => {
                                        setSelectedDay(isCurrentMonthYear ? dayjs().date() : null);
                                        setPage(1);
                                    }}
                                    options={Array.from(
                                        { length: dayjs(`${selectedYear}-${selectedMonthNum}`).daysInMonth() },
                                        (_, i) => {
                                            const day = i + 1;
                                            return { value: day, label: day.toString() };
                                        }
                                    )}
                                />
                                <Select
                                    style={{ flex: 1, height: '33px' }}
                                    value={selectedMonthNum}
                                    onChange={(value) => {
                                        setSelectedMonthNum(value);
                                        setPage(1);
                                        if (!(value === dayjs().month() + 1 && selectedYear === dayjs().year())) {
                                            setSelectedDay(null);
                                        }
                                    }}
                                    options={[
                                        { value: 1, label: "January" },
                                        { value: 2, label: "February" },
                                        { value: 3, label: "March" },
                                        { value: 4, label: "April" },
                                        { value: 5, label: "May" },
                                        { value: 6, label: "June" },
                                        { value: 7, label: "July" },
                                        { value: 8, label: "August" },
                                        { value: 9, label: "September" },
                                        { value: 10, label: "October" },
                                        { value: 11, label: "November" },
                                        { value: 12, label: "December" },
                                    ]}
                                />
                                <Select
                                    style={{ flex: 1, height: '33px' }}
                                    value={selectedYear}
                                    onChange={(value) => {
                                        setSelectedYear(value);
                                        setPage(1);
                                        if (!(selectedMonthNum === dayjs().month() + 1 && value === dayjs().year())) {
                                            setSelectedDay(null);
                                        }
                                    }}
                                    options={Array.from({ length: 20 }, (_, i) => {
                                        const year = dayjs().year() - 10 + i;
                                        return { value: year, label: year.toString() };
                                    })}
                                />
                            </div>
                        </Col> */
}
{
  /* </> */
}
{
  /* )} */
}

{
  /* Developer View (non-admin) */
}
{
  /* {authData?.roleId?.roleId !== 1 && (
                    <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                        <div className="flex flex-col sm:flex-row gap-2 w-full justify-end">
                            <Select
                                style={{ flex: 1, maxWidth: 120 }}
                                value={selectedMonthNum}
                                onChange={(value) => {
                                    setSelectedMonthNum(value);
                                    setPage(1);
                                    if (!(value === dayjs().month() + 1 && selectedYear === dayjs().year())) {
                                        setSelectedDay(null);
                                    }
                                }}
                                options={[
                                    { value: 1, label: "Jan" },
                                    { value: 2, label: "Feb" },
                                    { value: 3, label: "Mar" },
                                    { value: 4, label: "Apr" },
                                    { value: 5, label: "May" },
                                    { value: 6, label: "Jun" },
                                    { value: 7, label: "Jul" },
                                    { value: 8, label: "Aug" },
                                    { value: 9, label: "Sep" },
                                    { value: 10, label: "Oct" },
                                    { value: 11, label: "Nov" },
                                    { value: 12, label: "Dec" },
                                ]}
                            />
                            <Select
                                style={{ flex: 1, maxWidth: 100 }}
                                value={selectedYear}
                                onChange={(value) => {
                                    setSelectedYear(value);
                                    setPage(1);
                                    if (!(selectedMonthNum === dayjs().month() + 1 && value === dayjs().year())) {
                                        setSelectedDay(null);
                                    }
                                }}
                                options={Array.from({ length: 5 }, (_, i) => {
                                    const year = dayjs().year() - 2 + i;
                                    return { value: year, label: year.toString() };
                                })}
                            />
                        </div>
                    </Col>
                )} */
}
